import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import './AssetProcessingNotification.css';

/**
 * AssetProcessingNotification component displays the blue notification
 * matching the mockup design with:
 * - Blue background notification
 * - "Process these assets under [Client Name]" text
 * - Paperclip icon
 * - Asset count indicator
 */
const AssetProcessingNotification = ({
  campaign,
  assetCount,
  clientName,
  onDismiss,
  showTimestamp = true,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  // Extract client name from campaign or use provided clientName
  const getClientName = () => {
    if (clientName) return clientName;
    
    // Try to extract from campaign name (e.g., "July 4 Weekend Getaway - DV360" -> "July 4 Weekend Getaway")
    if (campaign?.name) {
      const parts = campaign.name.split(' - ');
      if (parts.length > 1) {
        return parts[0];
      }
      return campaign.name;
    }
    
    // Fallback to folder name or generic
    return campaign?.folder_name || 'Campaign Assets';
  };

  // Get asset count from campaign files or use provided count
  const getAssetCount = () => {
    if (assetCount !== undefined) return assetCount;

    // Count from campaign files
    if (campaign?.files && Array.isArray(campaign.files)) {
      return campaign.files.length;
    }

    // Default count
    return 3;
  };

  // Get file list with download URLs
  const getFileList = () => {
    if (campaign?.files && Array.isArray(campaign.files)) {
      return campaign.files.map(file => ({
        id: file.id,
        name: file.original_name || file.name || 'Unknown file',
        size: file.file_size || file.size,
        publicUrl: file.publicUrl,
        expiresAt: file.expiresAt,
        isExpired: file.expiresAt ? new Date(file.expiresAt) < new Date() : false
      }));
    }

    // Default file list for demo purposes
    return [
      { name: 'campaign_assets.xlsx', size: 1024000, publicUrl: null, isExpired: false },
      { name: 'creative_brief.pdf', size: 512000, publicUrl: null, isExpired: false },
      { name: 'media_plan.zip', size: 2048000, publicUrl: null, isExpired: false }
    ];
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    if (kb < 1024) {
      return `${Math.round(kb)}KB`;
    }
    const mb = kb / 1024;
    return `${Math.round(mb * 10) / 10}MB`;
  };

  // Format timestamp to show campaign creation time (e.g., "Today 9:53am" or "Dec 15 2:30pm")
  const formatTimestamp = () => {
    // Use campaign creation timestamp if available
    if (campaign?.created_at) {
      const createdDate = new Date(campaign.created_at);
      const now = new Date();

      // Check if it's today
      const isToday = createdDate.toDateString() === now.toDateString();

      const timeString = createdDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).toLowerCase();

      if (isToday) {
        return `Today ${timeString}`;
      } else {
        // For other days, show month/day and time
        const dateString = createdDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
        return `${dateString} ${timeString}`;
      }
    }

    // Fallback to current time if no campaign creation timestamp
    const timeString = new Date().toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).toLowerCase();
    return `Today ${timeString}`;
  };

  // Toggle expand/collapse
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Download a single file
  const downloadSingleFile = async (file) => {
    if (file.publicUrl && !file.isExpired) {
      // Direct download using existing URL
      window.open(file.publicUrl, '_blank');
    } else if (file.id && campaign?.id) {
      // Generate new download URL for expired or missing URLs
      try {
        const response = await fetch(`/api/v2/campaigns/${campaign.id}/files/${file.id}/download`);
        if (response.ok) {
          const data = await response.json();
          if (data.publicUrl) {
            window.open(data.publicUrl, '_blank');
          } else {
            console.error('Unable to generate download link for file:', file.name);
          }
        } else {
          console.error('Failed to download file:', file.name);
        }
      } catch (error) {
        console.error('Error downloading file:', file.name, error);
      }
    } else {
      console.error('Download not available for file:', file.name);
    }
  };

  const displayClientName = getClientName();
  const displayAssetCount = getAssetCount();

  return (
    <div className={`asset-processing-notification ${className}`}>
      {/* Timestamp - matching mockup */}
      {showTimestamp && (
        <div className="notification-timestamp">
          {formatTimestamp()}
        </div>
      )}

      <div className="notification-content">
        {/* Main Text */}
        <div className="notification-text">
          Process these assets under {displayClientName}
        </div>

        {/* Icon and Count */}
        <div
          className="notification-assets"
          onClick={toggleExpanded}
        >
          <FontAwesomeIcon icon={faPaperclip} className="paperclip-icon" />
          <span className="asset-count">{displayAssetCount}</span>
          <FontAwesomeIcon
            icon={isExpanded ? faChevronUp : faChevronDown}
            className="expand-icon"
          />
        </div>
      </div>

      {/* File List - Only show when expanded */}
      {isExpanded && (
        <div className="attachment-files-section">
          <div className="attachment-files-header">ATTACHED FILES</div>
          <div className="attachment-files-list">
            {getFileList().map((file, index) => (
              <div key={index} className={`attachment-file-row ${file.isExpired ? 'expired' : ''}`}>
                <div
                  className="file-icon-wrapper downloadable"
                  onClick={() => downloadSingleFile(file)}
                  title={file.isExpired ? 'Generate new download link' : 'Download file'}
                >
                  <FontAwesomeIcon icon={faPaperclip} className="file-row-icon" />
                </div>
                <div className="attachment-file-details">
                  <span className="attachment-file-name">{file.name}</span>
                  <span className="attachment-file-size">({formatFileSize(file.size)})</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Optional Dismiss Button */}
      {onDismiss && (
        <button
          className="dismiss-button"
          onClick={onDismiss}
          aria-label="Dismiss notification"
        >
          ×
        </button>
      )}
    </div>
  );
};

export default AssetProcessingNotification;
